/**
 * ProjectSearchPanel — inline-панель поиска проекта по коду (§4.2 шаг 3).
 *
 * Извлечена из удалённого `ProjectPicker.tsx`. Рендерится **прямо** в шаге 3
 * NewEntryFlow (без обёртки Sheet/Dialog) — состоит из:
 *  - крупного displays-блока с введёнными цифрами,
 *  - debounced (200ms) typeahead через `searchProjects`,
 *  - кнопки «Записать как есть ⚠» при `≥3 цифр` без матчей → AlertDialog
 *    confirmation → возвращает `{ kind: 'raw', code }`,
 *  - NumericPad внизу.
 *
 * Контракт значения:
 *  - `value.kind === 'id'`  → известный project_id,
 *  - `value.kind === 'raw'` → свободный код для проверки руководителем.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";

import { Button } from "@/components/ui/button";
import { NumericPad } from "@/components/field/NumericPad";
import { searchProjects } from "@/api/field-timesheet";
import type { ProjectSearchHit } from "@/api/field-timesheet";

export type ProjectSearchValue =
  | { kind: "id"; projectId: number; code: string | null; name: string }
  | { kind: "raw"; code: string };

const DEBOUNCE_MS = 200;
const MIN_DIGITS_FOR_RAW = 3;

export interface ProjectSearchPanelProps {
  onPick: (value: ProjectSearchValue) => void;
  "data-testid"?: string;
}

export function ProjectSearchPanel({
  onPick,
  "data-testid": testId = "project-search-panel",
}: ProjectSearchPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");
  const [confirmRaw, setConfirmRaw] = React.useState<string | null>(null);

  const debounced = useDebouncedValue(query, DEBOUNCE_MS);
  const enabled = debounced.length > 0;
  const hits = useQuery({
    queryKey: ["field-project-search", debounced] as const,
    queryFn: () => searchProjects(debounced),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const results = enabled ? (hits.data ?? []) : [];
  const isEmpty = enabled && !hits.isPending && results.length === 0;
  const canRaw = isEmpty && debounced.length >= MIN_DIGITS_FOR_RAW;

  function handlePickHit(hit: ProjectSearchHit): void {
    onPick({ kind: "id", projectId: hit.id, code: hit.code, name: hit.name });
  }

  function handleConfirmRaw(code: string): void {
    setConfirmRaw(null);
    onPick({ kind: "raw", code });
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid={testId}>
      {/* Display strip — крупно показывает что набрано (фиксированная высота сверху) */}
      <div
        className="flex h-14 shrink-0 items-baseline justify-center"
        data-testid={`${testId}-display`}
        style={{
          background: "var(--bg-panel-1)",
          border: "1px solid var(--rule-line)",
          borderRadius: "var(--tile-radius)",
        }}
      >
        <span
          className="font-display font-semibold"
          style={{
            color: query.length === 0 ? "var(--fg-3)" : "var(--fg-1)",
            fontFamily: "var(--font-display)",
            fontFeatureSettings: '"tnum" 1, "lnum" 1',
            fontSize: 32,
            letterSpacing: "0.08em",
          }}
        >
          {query.length === 0 ? "—" : query}
        </span>
      </div>

      {/* Results / hints — занимают всё свободное место над pad'ом и
          скроллятся внутри. min-h-0 нужен чтобы flex-1 разрешил overflow
          сжимать контейнер ниже sum(content). */}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid={`${testId}-results`}
        style={{ borderTop: "1px solid var(--rule-line)" }}
      >
        {!enabled && (
          <p
            className="px-1 pt-3 font-condensed text-[11px] uppercase tracking-[0.16em]"
            style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
          >
            {t("field.entryFlow.step2.searchHint")}
          </p>
        )}
        {enabled && hits.isPending && (
          <div className="flex items-center justify-center py-6">
            <Loader2
              className="h-5 w-5 animate-spin"
              style={{ color: "var(--fg-3)" }}
              aria-hidden="true"
            />
          </div>
        )}
        {results.map((hit) => (
          <button
            key={hit.id}
            type="button"
            onClick={() => handlePickHit(hit)}
            data-testid={`${testId}-result-${hit.id}`}
            className="flex items-baseline gap-3 px-2 py-3 text-left transition-colors"
            style={{ borderBottom: "1px solid var(--rule-line)", minHeight: 52 }}
          >
            <span
              className="font-condensed text-[16px] font-semibold uppercase tracking-[0.06em]"
              style={{
                color: "var(--fg-1)",
                fontVariantNumeric: "tabular-nums",
                minWidth: 56,
              }}
            >
              {hit.code}
            </span>
            <span className="flex-1 truncate text-[13px]" style={{ color: "var(--fg-2)" }}>
              {hit.name}
            </span>
            {hit.customer_name && (
              <span
                className="hidden shrink-0 text-[11px] sm:inline"
                style={{ color: "var(--fg-3)" }}
              >
                {hit.customer_name}
              </span>
            )}
          </button>
        ))}
        {isEmpty && !canRaw && (
          <p
            className="px-1 pt-3 font-condensed text-[11px] uppercase tracking-[0.16em]"
            style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            data-testid={`${testId}-empty`}
          >
            {t("field.entryFlow.step2.emptyShort")}
          </p>
        )}
      </div>

      {/* Bottom-pinned блок: unknown-CTA + pad. shrink-0 + mt-auto держат
          группу у нижней границы независимо от количества результатов,
          чтобы NumericPad не «прыгал» при изменении высоты results. */}
      <div className="mt-auto flex shrink-0 flex-col gap-3">
        {canRaw && (
          <button
            type="button"
            onClick={() => setConfirmRaw(debounced)}
            data-testid={`${testId}-raw`}
            className="flex items-center justify-center gap-2 px-3 py-3 transition-colors active:translate-y-[1px]"
            style={{
              background: "#fff7ed",
              color: "#9a3412",
              border: "1px solid #f59e0b",
              borderRadius: "var(--radius-button)",
              minHeight: 56,
            }}
          >
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <span
              className="font-condensed text-[13px] font-semibold uppercase tracking-[0.08em]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("field.entryFlow.step2.recordAsIs")}
            </span>
          </button>
        )}

        {/* Ввод № проекта — единый тач-`NumericPad` на всех поверхностях. На
            тач-устройстве он убирает глючную системную клавиатуру, на десктопе
            принимает и клики мышью, и цифры с физической клавиатуры (`NumericPad`
            ловит onKeyDown). Прежний свап на `<input>` для desktop-dock откатан —
            `<Sheet>` дока на телефоне руководителя давал keyboard-hostile поле. */}
        <div className="flex justify-center">
          <NumericPad
            length={{ min: 1, max: 8 }}
            value={query}
            onChange={setQuery}
            onSubmit={() => {
              // Submit ✓ на паде — выбираем единственный матч, если он есть.
              if (results.length === 1) handlePickHit(results[0]);
            }}
            data-testid={`${testId}-pad`}
            aria-label={t("field.entryFlow.step2.padAria")}
          />
        </div>
      </div>

      <UnknownProjectConfirm
        code={confirmRaw}
        onCancel={() => setConfirmRaw(null)}
        onConfirm={handleConfirmRaw}
        testId={testId}
      />
    </div>
  );
}

interface UnknownProjectConfirmProps {
  code: string | null;
  onCancel: () => void;
  onConfirm: (code: string) => void;
  testId: string;
}

function UnknownProjectConfirm({
  code,
  onCancel,
  onConfirm,
  testId,
}: UnknownProjectConfirmProps): React.ReactElement {
  const { t } = useTranslation();
  const open = code !== null;
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/30"
          data-testid={`${testId}-confirm-overlay`}
        />
        <AlertDialogPrimitive.Content
          className="fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 p-4 outline-none"
          style={{
            background: "var(--bg-panel-0)",
            border: "1px solid #f59e0b",
            borderRadius: "var(--radius-card)",
            boxShadow: "var(--shadow-plate)",
          }}
          data-testid={`${testId}-confirm`}
        >
          <AlertDialogPrimitive.Title
            className="font-condensed text-[15px] font-semibold uppercase tracking-[0.06em]"
            style={{ color: "var(--fg-1)" }}
          >
            {t("field.entryFlow.step2.unknownProjectTitle")}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description
            className="text-[13px] leading-snug"
            style={{ color: "var(--fg-2)" }}
          >
            {t("field.entryFlow.step2.unknownProjectBody", { code: code ?? "" })}
          </AlertDialogPrimitive.Description>
          <div className="flex items-center justify-end gap-2">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="ghost" size="sm" data-testid={`${testId}-confirm-cancel`}>
                {t("field.entryFlow.step2.unknownProjectCancel")}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <Button
                variant="default"
                size="sm"
                onClick={() => code !== null && onConfirm(code)}
                data-testid={`${testId}-confirm-ok`}
                style={{ background: "#b45309", color: "#fff" }}
              >
                {t("field.entryFlow.step2.unknownProjectOk")}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
